import type { QuotationDocumentData } from '@/quotation/document/types';
import { getLabels } from '@/quotation/document/labels';
import { formatMoney } from '@/quotation/document/formatters';

interface Props { data: QuotationDocumentData }

interface TotalRowProps {
  label: string;
  value: string;
  variant?: 'normal' | 'subtotal' | 'total';
}

function TotalRow({ label, value, variant = 'normal' }: TotalRowProps) {
  return (
    <div
      className={`flex items-baseline justify-between gap-4 ${
        variant === 'total'
          ? 'mt-2 pt-3 border-t-2 border-ink-700'
          : variant === 'subtotal'
            ? 'mt-1.5 pt-3 border-t border-neutral-300'
            : 'py-1'
      }`}
    >
      <span
        className={`${
          variant === 'total'
            ? 'text-sm font-bold text-ink-700 uppercase tracking-wide'
            : variant === 'subtotal'
              ? 'text-sm font-semibold text-neutral-800'
              : 'text-sm text-neutral-500'
        }`}
      >
        {label}
      </span>
      <span
        className={`text-right tabular-nums ${
          variant === 'total'
            ? 'text-lg font-extrabold text-ink-700'
            : variant === 'subtotal'
              ? 'text-sm font-bold text-neutral-900'
              : 'text-sm text-neutral-700'
        }`}
      >
        {value}
      </span>
    </div>
  );
}

export default function HtmlDocTotals({ data }: Props) {
  const L = getLabels(data.documentLocale);
  const c = data.currency;
  const lo = data.documentLocale;

  return (
    <div className="px-8 md:px-12 mt-7">
      <div className="flex justify-end">
        <div className="w-full sm:w-[420px]">
          {data.productAmount != null && (
            <TotalRow label={L.productAmount} value={formatMoney(data.productAmount, c, lo)} />
          )}
          {data.finishingAmount != null && data.finishingAmount > 0 && (
            <TotalRow label={L.finishingAmount} value={formatMoney(data.finishingAmount, c, lo)} />
          )}
          {data.freightAmount != null && (
            <TotalRow label={L.freight} value={formatMoney(data.freightAmount, c, lo)} />
          )}
          {data.customsClearanceAmount != null && (
            <TotalRow label={L.customsClearance} value={formatMoney(data.customsClearanceAmount, c, lo)} />
          )}
          {data.insuranceAmount != null && (
            <TotalRow label={L.insurance} value={formatMoney(data.insuranceAmount, c, lo)} />
          )}
          {data.otherCharges != null && (
            <TotalRow label={L.otherCharges} value={formatMoney(data.otherCharges, c, lo)} />
          )}

          {data.subtotal != null && data.productAmount != null && data.finishingAmount != null && (
            <TotalRow label={L.subtotal} value={formatMoney(data.subtotal, c, lo)} variant="subtotal" />
          )}

          <TotalRow
            label={L.total}
            value={formatMoney(data.total, c, lo)}
            variant="total"
          />

          {data.exchangeRate != null && (
            <p className="text-xs text-neutral-400 mt-2 text-right">
              {L.exchangeRate}: {data.exchangeRateNote || `USD 1 = ${data.currency} ${data.exchangeRate}`}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}